import React, { useCallback, useEffect, useRef, useState } from "react";
import { getRepuestosMe } from "../api";
import { errMsg } from "../utils";
import RepuestosInicio from "./RepuestosInicio";
import RepuestosAutorizaciones from "./RepuestosAutorizaciones";
import RepuestosDetalle from "./RepuestosDetalle";
import "./Repuestos.css";

const LOGO = process.env.PUBLIC_URL + "/fs-black.png";

/* Íconos de línea (mismo estilo que el menú de convenios). */
const IconInicio = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 10.7 12 4l9 6.7" /><path d="M5.5 9.5V20h13V9.5" /><path d="M10 20v-5h4v5" />
  </svg>
);
const IconAutorizaciones = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 3.5h6a1 1 0 0 1 1 1V6H8V4.5a1 1 0 0 1 1-1Z" />
    <path d="M8 5H6a1 1 0 0 0-1 1v13.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1h-2" />
    <path d="m9 13.5 2 2 4-4.5" />
  </svg>
);

const NAV = [
  { key: "inicio", label: "Inicio", icon: <IconInicio /> },
  { key: "lista", label: "Autorizaciones", icon: <IconAutorizaciones /> },
];

const TITLES = {
  inicio: "Inicio",
  lista: "Autorizaciones de repuestos",
  detalle: "Autorización de repuestos",
};

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "P";
  return (parts[0][0] + (parts[1] ? parts[1][0] : "")).toUpperCase();
}

/**
 * Portal de los PROVEEDORES DE REPUESTOS (permiso REPUESTOS_PORTAL).
 * Mismo shell que el de convenios (barra lateral, header y tabbar en móvil),
 * con su propio menú. Pantallas: inicio | lista | detalle
 */
export default function RepuestosPortal({ onLogout }) {
  const [me, setMe] = useState(null);
  const [meError, setMeError] = useState("");
  const [meLoading, setMeLoading] = useState(true);
  const [screen, setScreen] = useState("inicio");
  const [filtro, setFiltro] = useState("");
  const [selId, setSelId] = useState(null);
  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);

  const loadMe = useCallback(async (silent) => {
    if (!silent) {
      setMeLoading(true);
      setMeError("");
    }
    try {
      setMe(await getRepuestosMe());
      setMeError("");
    } catch (e) {
      // 401 lo maneja el interceptor (vuelve al login).
      if (!silent && e?.response?.status !== 401) {
        setMeError(errMsg(e, "No se pudo cargar tu cuenta. Intenta de nuevo."));
      }
    } finally {
      if (!silent) setMeLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe(false);
  }, [loadMe]);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  function showToast(msg) {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 3600);
  }

  function go(next) {
    setScreen(next);
    window.scrollTo(0, 0);
  }

  function openList(estado) {
    setFiltro(estado || "");
    go("lista");
  }

  function openDetail(id) {
    setSelId(id);
    go("detalle");
  }

  const supplierName = me?.supplier?.name || "Proveedor";
  const userSub = me?.user?.name || me?.user?.email || "Proveedor de repuestos";
  const showNav = NAV.some((n) => n.key === screen);

  return (
    <div className="prov-shell">
      {/* ---------- Barra lateral (escritorio) ---------- */}
      <aside className="prov-sidebar no-print">
        <div className="brand">
          <span className="brand-logo"><img src={LOGO} alt="ALAS" /></span>
          <div className="brand-text">
            <span className="brand-name">ALAS</span>
            <span className="brand-tag">Proveedores</span>
          </div>
        </div>

        <nav className="side-menu">
          <div className="menu-eyebrow">Repuestos</div>
          {NAV.map((n) => (
            <button
              key={n.key}
              className={`side-link ${screen === n.key || (screen === "detalle" && n.key === "lista") ? "active" : ""}`}
              onClick={() => go(n.key)}
            >
              <span className="side-ico">{n.icon}</span>
              {n.label}
            </button>
          ))}
        </nav>

        <div className="side-footer">
          <div className="user-card">
            <span className="user-avatar">{initials(supplierName)}</span>
            <div className="user-meta">
              <span className="user-name" title={supplierName}>{supplierName}</span>
              <span className="user-sub">{userSub}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* ---------- Main ---------- */}
      <div className="prov-main">
        <header className="prov-header no-print">
          <div className="ph-left">
            <span className="ph-brand-mobile"><img src={LOGO} alt="ALAS" /></span>
            <div className="ph-brand-mobile-text">
              <span className="n">ALAS</span>
              <span className="t">Proveedores</span>
            </div>
            <h1 className="ph-title">{TITLES[screen] || "Portal de Proveedores"}</h1>
          </div>
          <div className="ph-right">
            <div className="ph-company">
              <span className="ph-company-lbl">Proveedor</span>
              <span className="ph-company-name" title={supplierName}>{supplierName}</span>
            </div>
            <button className="ph-logout" onClick={onLogout}>Salir</button>
          </div>
        </header>

        <main className="prov-content">
          {screen === "inicio" && (
            <RepuestosInicio
              me={me}
              loading={meLoading}
              meError={meError}
              onRetry={() => loadMe(false)}
              onOpenList={openList}
            />
          )}

          {screen === "lista" && (
            <RepuestosAutorizaciones
              filtro={filtro}
              onFiltro={setFiltro}
              counts={me?.counts}
              onOpen={openDetail}
              onRefreshCounts={() => loadMe(true)}
            />
          )}

          {screen === "detalle" && selId != null && (
            <RepuestosDetalle
              id={selId}
              onBack={() => go("lista")}
              onToast={showToast}
              onChanged={() => loadMe(true)}
            />
          )}
        </main>
      </div>

      {/* ---------- Tabbar inferior (móvil) ---------- */}
      {showNav && (
        <nav className="prov-tabbar no-print">
          {NAV.map((n) => (
            <button
              key={n.key}
              className={screen === n.key ? "on" : ""}
              onClick={() => go(n.key)}
            >
              <span className="tico">{n.icon}</span>
              {n.label}
            </button>
          ))}
        </nav>
      )}

      {toast && <div className="toast no-print">{toast}</div>}
    </div>
  );
}
